export enum Lang {
  zh = 'zh',
  en = 'en',
  vn = 'vn',
  vi = 'vi',
  id = 'id',
  ms = 'ms',
  th = 'th',
  jp = 'jp',
  kr = 'kr',
  es = 'es',
  pk = 'pk',
  mm = 'mm',
  bd = 'bd',
  hant = 'hant',
  in = 'in',
  kh = 'kh',
  pt = 'pt',
  uz = 'uz',
}

export enum OddSelection {
  euro = 'euro',
  indo = 'indo',
  hongkong = 'hongkong',
  malay = 'malay',
}

export enum ArenaCountries {
  PH,
  DR,
  MX,
  PE,
}

export enum PopupSize {
  small = '300px',
  default = '500px',
  large = '800px',
  xlarge = '1140px',
}

export enum ScreenSize {
  desktop = 'desktop',
  pad = 'pad',
  phone = 'phone',
}

export enum GameType {
  fights = 'fights',
  fight_results = 'fight_results',
  arena_stream = 'arena_stream',
  main = 'main',
}

export enum GameStatus {
  start = 0,
  betOpen = 100,
  betClosed = 200, // no more bet announcer
  preResulting1 = 210, // for game treasure (optional), csgo run
  preResulting2 = 220, // for game pre-result animation (optional)
  resulting = 300,
  final = 400,
}

export enum MgWSState {
  betting = 'BETTING',
  stopBetting = 'STOP_BETTING',
  showResult = 'SHOW_RESULT',
}

export enum MgBetStatus {
  init = 0,
  confirmed = 200,
  pending = 310,
  rejected = 320,
  accepted = 330,
  settled = 400,
}

export enum CurrencyCode {
  VND = 'VND',
  IDR = 'IDR',
  USD = 'USD',
  THB = 'THB',
  EUR = 'EUR',
  HKD = 'HKD',
  MYR = 'MYR',
  JPY = 'JPY',
  KHR = 'KHR',
  PHP = 'PHP',
}

export interface IMaintenance {
  maintenance: boolean;
  from: string;
  to: string;
}

export interface IErrorHandler {
  statusCode: number;
  errorMessage: string;
  showPopup: boolean;
}

export enum ChartOptions {
  'General' = 'General',
  'Road' = 'Road',
}

export enum ChartTypes {
  general = 'general',
  summary = 'summary',
  road = 'road',
}

export enum SmallChartTypes {
  bigEye = 'bigEye',
  smallRoad = 'smallRoad',
  cockroachRoad = 'cockroachRoad',
}

export enum ChartDataType {
  image = 'image',
  numberImage = 'numberImage',
  text = 'text',
}

export enum SelectionTypes {
  banker = 'banker',
  draw = 'draw',
  player = 'player',
}

/*
 * `bet_type` for Tài Xỉu, as the backend defines it (TAIXIU_*_TICKET_BET_TYPE).
 * Sent as-is to the bet endpoint, and the numbering the board, odds and rail
 * work in. Tickets placed before the renumbering still come back with the
 * LegacyTaiXiuBetType values -- read a ticket's `betType` through
 * `normalizeTaiXiuTicketBetType`, never against either enum directly.
 */
export enum TaiXiuBetType {
  bigSmall = 11,
  domino = 12,
  double = 13,
  oddEven = 14,
  single = 15,
  triple = 16,
  total4 = 17,
  total5 = 18,
  total6 = 19,
  total7 = 20,
  total8 = 21,
  total9 = 22,
  total10 = 23,
  total11 = 24,
  total12 = 25,
  total13 = 26,
  total14 = 27,
  total15 = 28,
  total16 = 29,
  total17 = 30,
}

// The numbering before the backend moved the markets to start at 11.
export enum LegacyTaiXiuBetType {
  bigSmall = 1,
  domino = 2,
  double = 3,
  oddEven = 4,
  single = 5,
  triple = 6,
  total4 = 7,
  total17 = 20,
}

const LEGACY_BET_TYPE_OFFSET = TaiXiuBetType.bigSmall - LegacyTaiXiuBetType.bigSmall;

/*
 * A ticket's `bet_type`, in either numbering, as the TaiXiuBetType it was
 * placed with. The two overlap (legacy total8..total13 = new bigSmall..triple),
 * so the selection settles it: a total's selection is its own total (4..17),
 * and each numbering puts it at a fixed offset from that.
 */
export const normalizeTaiXiuTicketBetType = (betType: unknown, selection: unknown): TaiXiuBetType | undefined => {
  if (typeof betType === 'string' && betType.trim() !== '' && Number.isNaN(Number(betType))) {
    return taiXiuMarketNameBetType(betType, selection);
  }
  const type = Number(betType);
  if (!Number.isInteger(type)) return undefined;

  const total = Number(selection);
  if (Number.isInteger(total) && total >= 4 && total <= 17) {
    const current = TaiXiuBetType.total4 + total - 4;
    if (type === current) return current;
    if (type === LegacyTaiXiuBetType.total4 + total - 4) return current;
  }

  if (type >= TaiXiuBetType.bigSmall && type <= TaiXiuBetType.triple) return type;
  if (type >= LegacyTaiXiuBetType.bigSmall && type <= LegacyTaiXiuBetType.triple) return type + LEGACY_BET_TYPE_OFFSET;
  return undefined;
};

const TAI_XIU_MARKET_NAMES: { [name: string]: TaiXiuBetType } = {
  single: TaiXiuBetType.single,
  double: TaiXiuBetType.double,
  triple: TaiXiuBetType.triple,
  domino: TaiXiuBetType.domino,
  big_small: TaiXiuBetType.bigSmall,
  odd_even: TaiXiuBetType.oddEven,
};

/*
 * A market named in words, as the tickets endpoint can send it: "single",
 * "SINGLE_3", "single-dice", "total_13", "Big Small"... The name decides the
 * market; a total takes its value from the selection (4..17).
 */
export const taiXiuMarketNameBetType = (name: unknown, selection: unknown): TaiXiuBetType | undefined => {
  const key = String(name === undefined || name === null ? '' : name)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (!key) return undefined;
  if (key === 'total' || key.startsWith('total_') || key === 'sum' || key.startsWith('sum_')) {
    const total = Number(selection);
    return Number.isInteger(total) && total >= 4 && total <= 17 ? TaiXiuBetType.total4 + total - 4 : undefined;
  }
  if (TAI_XIU_MARKET_NAMES[key] !== undefined) return TAI_XIU_MARKET_NAMES[key];
  // "single_3", "single_dice", "triple_any"...: the market's own name first.
  const prefix = Object.keys(TAI_XIU_MARKET_NAMES).find((market) => key.startsWith(`${market}_`));
  return prefix ? TAI_XIU_MARKET_NAMES[prefix] : undefined;
};

export enum TaiXiuSelection {
  small = 'small',
  big = 'big',
  odd = 'odd',
  even = 'even',
}

export interface ISmallMapSourceData {
  val: String | number;
  x: number;
  y: number;
}

export enum PopupPage {
  main = 'Main',
  limits = 'Limits',
  history = 'History',
  statement = 'Statement',
  settings = 'Settings',
  notification = 'Notification',
  schedule = 'Schedules',
  scheduleDetail = 'Schedule details',
  help = 'Help',
  betDetail = 'Bet detail',
  tnc = 'Tnc',
  jackpot = 'jackpot',
  jackpotHistory = 'Jackpot history',
  jackpotHelp = 'Jackpot help',
  jackpotLuckyDraw = 'Lucky draw',
  jackpotPhysicalHelp = 'Jackpot prize',
}

export enum WatermarkMovement {
  static = 0,
  random = 1,
  marquee = 2,
}

export interface IGeneral {
  inputPanelScaleRatio: {
    vertical: number;
    landscape: number;
  };
  balance: {
    value: number;
    futureDiff: number;
  };
  token: string;
  member: {
    displayName: string;
    currencyDisplayCode: string;
    currencySymbol: string;
    exchangeRate: string;
  };
  errorHandler: {
    errorMessage: string;
    showPopup: boolean;
  };
  arenas: IArena[];
  selectedArena: IArena;
  config: IConfig;
  videoAutoScale: boolean;
  theme: string;
  constrols: IControl;
  lastIframeUrl: string;
  streamUrl: string;
  streamError: string;
  maintenance: IMaintenance;
  streamVersion: number;
  streamWatermarkData: IStreamWatermarkData;
  streamDrmData: IStreamDrmData;
  // floatingVideo: boolean
  serverTimeDiff?: number;
  selectedArenaCode: string;
  focus: string;
  pipDisabled: boolean;
  // arenaId -> that arena's stream payload, so two players can stream at once.
  arenaStreams: { [arenaId: string]: any };
  arenaBetChips: { [arenaId: string]: IArenaBetChips };
  lobbyUrl: string;
  isMuted: boolean;
  binaryDisabled: boolean;
  clientVersion: number;
}

// GET/PATCH /arenas/:id/bet-chips
export interface IArenaBetChips {
  betChips: number[];
  customBetChip: number;
  minBetChip: number;
  maxBetChip: number;
}

export interface IStreamWatermarkData {
  url: string;
  watermark: string;
  watermarkFrequency: number;
  watermarkMovement: WatermarkMovement;
  watermarkType: number;
  watermarkImageUrl: string;
  watermarkFontSize?: number | string;
}

export interface IStreamDrmData {
  drmLicenseUrl: string;
  drmCertUrl: string;
}

export interface IMaintenance {
  m: boolean;
  from: string;
  to: string;
}

export interface IControl {
  historySelection: string;
}

export interface IConfig {
  enableJackpot: boolean;
  minAmountPerFight: number;
  mainLimitAmountPerFight: number;
  drawLimitAmountPerFight: number;
  numTicketPerFight: number;
  language: string;
  languageList: string[];
  availableOddsType: string[];
  oddsType: string;
  betChips: number[];
  withCommission: boolean;
  hasCommission: boolean;
  timezone: string;
  disableLuckyDrawPrizePayout: boolean;
  displayLuckyDrawPrizes: boolean;
  customBetChip: number;
  floatVideo: boolean;
  debug: boolean;
}

export interface IBetData {
  banker: number;
  draw: number;
  player: number;
}

// export interface IBetDisplayData {
//   banker: IBetDisplayDataSingle;
//   draw: IBetDisplayDataSingle;
//   player: IBetDisplayDataSingle;
// }

// export interface IBetDisplayDataSingle {
//   totalBetAmount?: number;
//   peopleCount?: number;
//   ratio?: number;
//   hasDraw?: boolean;
//   odds: number;
//   confirmedBetAmount: number;
// }

export interface IFightResponse {
  id: string;
  fight_number: number;
  status: string;
  result: string;
  stream_url: string;
  replay_url: string;
  is_bets_paused: boolean;
  selections: [
    {
      name: string;
      display_name: string;
      odds: number;
    }
  ];
  match_day: {
    id: string;
    code: string;
    start_datetime: string;
    fight_type: string;
    arena: {
      id: string;
      name: {
        default: string;
        vn: string;
      };
      country: string;
      code: string;
    };
  };
}

export interface IFight {
  id: string;
  fightNumber: number;
  status: string;
  result: string;
  streamUrl: string;
  replayUrl: string;
  isBetsPaused: boolean;
  selections: IFightSelections[];
  matchDay: IMatchDay;
  confirmedDateTime: string;
}

export interface IFightChartTotal {
  playerCount: number;
  bankerCount: number;
  drawCount: number;
}

export interface IFightSelections {
  name: string;
  displayName: string;
  odds: number;
  totalBetAmount: number;
  totalBetCount: number;
}

export interface IMatchDay {
  id: string;
  code: string;
  startDatetime: string;
  fightType: string;
  arena: IArena;
}

export interface IArena {
  index?: number;
  id: string;
  name: {
    default: string;
    vn?: string;
  };
  country: string;
  code: string;
  currentMatchDayId?: string;
  fightType?: string;
  fightStatus?: string;
  isReverse: boolean;
}

export enum Orientation {
  landscape = 'landscape',
  portrait = 'portrait',
}

export interface IPagination {
  page: number;
  pageSize: number;
}

export enum FightStatus {
  created,
  confirmed,
  closing,
  closed,
  started,
  ended,
  declared,
  approved,
  uncancelling,
  cancelling,
  cancelled,
  unprocessing,
  processing,
  processed,
  adjustingCloseDateTime,
  hold,
}

export const CURRENT_FIGHT_STATUSES: string[] = [
  FightStatus.confirmed,
  FightStatus.closing,
  FightStatus.closed,
  FightStatus.started,
  FightStatus.ended,
  FightStatus.declared,
].map((s) => FightStatus[s]);

export interface ITicket {
  id: string;
  sid?: string;
  createdDateTime: string;
  selection: string;
  odds: number;
  amount: number;
  result: string;
  winLossAmount: number;
  taxAmount: number;
  fight: IFight;
  earnings?: number;
  status?: number;
  betType?: number;
  commission?: number;
  // A multi-selection ticket's own bets, one per selection (`sub_tickets`).
  subTickets?: ITicketSubTicket[];
}

// One selection of a multi-selection ticket. It has no `fight`: the parent's.
export interface ITicketSubTicket {
  id: string;
  sid?: string;
  createdDateTime?: string;
  selection: string;
  odds: number | string;
  amount: number | string;
  result: string;
  winLossAmount: number | string;
  status?: number;
  betType?: number;
  taxAmount?: number | string;
  commission?: number | string;
  // Read from the parent's "4:31|17:31" summary: no stake or W/L of its own.
  derived?: boolean;
}

export interface ITicketTotal {
  date: string;
  winLossAmount: number;
  jackpotAmount: number;
  anteAmount: number;
  commissionAmount: number;
  totalAmount: number;
  ticketCount?: number;
  earnings?: number;
}

export interface IBetHistoryData {
  date: string;
  dateVal?: string;
  wl: number;
  type: string;
  id?: string;
  bet?: number;
  jackpot?: number;
  ante?: number;
  commission?: number;
  total?: number;
  earnings?: number;
}

export interface IJackpotTicket {
  id: string;
  sid: string;
  createdDateTime: string;
  jackpotId: string;
  rebate: number;
  isSeen: boolean;
  jackpotTicket: IJackpotTicketData;
  jackpot: IJackpot;
}

export interface IJackpotTicketData {
  id: string;
  jackpotId: string;
  name: string;
  color: string;
  amount: number;
  redemptionOrder: number;
  jackpotAmount: number;
}
export interface IJackpotPastWinner {
  id: string;
  jackpotId: string;
  rank: number;
  displayName: string;
  amount: number;
  isMe: boolean;
}

export interface IJackpot {
  id: string;
  status: number;
  type: number;
  startDateTime: string;
  endDateTime: string;
  nextTicketAmount: number;
  currentAmount: number;
  drawNum: number;
  memberCurrentAmount: number;
  memberNextTicketAmount: number;
  name: string;
  nextAmount: number;
  nextDateTime: string;
  prevAmount: number;
  prevDateTime: string;
}

export interface IMenu {
  isPopupOpen: boolean;
  isJackpotPopupOpen: boolean;
  activePopupPage: PopupPage;
  isErrorPopupOpen: boolean;
  errorHeading: string;
  errorMsg: string;
}

export interface IAnnouncement {
  message: string;
  publishDateTime: string;
}

export interface ISchedule {
  id: string;
  createdDateTime: string;
  modifiedDateTime: string;
  matchStartDateTime: string;
  country: string;
  expectedNumFights: number;
  eventName: string;
  eventDescription: string;
  matchDay: string;
  imageUrl: string;
  externalLink: string;
  eventType: string;
}

export enum EventType {
  future = 'future',
  current = 'current',
  past = 'past',
}

export enum NumericInputType {
  int = 'int',
  float = 'float',
  decimal = 'decimal', // for ios decimal
}

export enum KeyDownKey {
  space = ' ',
  backspace = 'Backspace',
  arrowLeft = 'ArrowLeft',
  arrowRight = 'ArrowRight',
  dot = '.',
  comma = ',', // for ios decimal
  delete = 'Delete',
  digit0 = 'Digit0',
  digit1 = 'Digit1',
  digit2 = 'Digit2',
  digit3 = 'Digit3',
  digit4 = 'Digit4',
  digit5 = 'Digit5',
  digit6 = 'Digit6',
  digit7 = 'Digit7',
  digit8 = 'Digit8',
  digit9 = 'Digit9',
}
